import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  CalendarIcon,
  ShoppingCart,
  Truck,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Bot,
  User,
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  Package,
  Search,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Download,
  FileText
} from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, addWeeks, subWeeks, isSameDay, isSameMonth, eachDayOfInterval, getDay } from "date-fns";
import { formatDateDDMonYY } from "@/utils/dateFormat";

export default function Reports() {
  const [viewMode, setViewMode] = useState("week");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [returnsData, setReturnsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [repostDialogOpen, setRepostDialogOpen] = useState(false);
  const [repostingOrder, setRepostingOrder] = useState(null);
  const [reposting, setReposting] = useState(false);
  const [ordersExpanded, setOrdersExpanded] = useState(false);
  const [dispatchExpanded, setDispatchExpanded] = useState(false);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  
  // Edit state
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchProducts();
    fetchReturns();
    fetchRecentInvoices();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
    }
  };

  const fetchReturns = async () => {
    try {
      const response = await axios.get(`${API}/returns`);
      setReturnsData(response.data || []);
    } catch (error) {
    }
  };

  const fetchRecentInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices/recent?limit=5`);
      setRecentInvoices(response.data || []);
    } catch (error) {
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    setDownloadingInvoice(invoice.id);
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = invoice.filename || `${invoice.dispatch_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${invoice.filename}`);
    } catch (error) {
      toast.error("Failed to download invoice");
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Get orders for a specific day (by order_date)
  const getOrdersForDay = (date) => {
    return orders.filter(order => {
      const orderDate = new Date(order.order_date || order.created_at);
      return isSameDay(orderDate, date);
    });
  };

  // Get dispatches for a specific day (by dispatched_at date)
  const getDispatchesForDay = (date) => {
    return orders.filter(order => {
      if (order.status !== 'dispatched' || !order.dispatched_at) return false;
      const dispatchDate = new Date(order.dispatched_at);
      return isSameDay(dispatchDate, date);
    });
  };

  // Get returns for a specific day (by return_date)
  const getReturnsForDay = (date) => {
    return returnsData.filter(r => {
      if (!r.return_date) return false;
      const retDate = new Date(r.return_date + 'T00:00:00');
      return isSameDay(retDate, date);
    });
  };

  // Get calendar days for month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday start
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [selectedDate]);

  // Get week days for week view
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
    const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [selectedDate]);

  // Format currency with full Indian format (₹3,11,467/-)
  const formatCurrency = (value) => {
    const formatted = new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(value || 0);
    return `₹${formatted}/-`;
  };

  // AVRG calculation helpers
  const NINE_KG_GRADES = ['JH', 'SJH', 'JJH', 'W150', 'W180', '180', 'A180', 'PG'];
  const getPackingKg = (item) => NINE_KG_GRADES.includes((item.grade || '').toUpperCase().trim()) ? 9 : 10;
  const getOrderTotalKgs = (order) => (order?.items || []).reduce((s, i) => s + ((i.qty || 0) * getPackingKg(i)), 0);

  // Get display value for an order (use invoice_value when discount applies)
  // IMPORTANT: This must be defined before getDaySummary which uses it
  const getOrderDisplayValue = (order) => {
    const discountTerms = ['ADV', 'DDAP', 'DDAP RECVD'];
    const pt = (order.payment_terms || '').toUpperCase();
    if (discountTerms.includes(pt) && order.total_discount > 0 && order.invoice_value) {
      return order.invoice_value;
    }
    return order.total_value || order.total_price || 0;
  };

  // Calculate day summary - orders by order_date, dispatches by dispatched_at
  const getDaySummary = (date) => {
    const dayOrders = getOrdersForDay(date);
    const dayDispatches = getDispatchesForDay(date);
    const dayReturns = getReturnsForDay(date);
    const manualOrders = dayOrders.filter(o => o.source !== 'bot' && !o.order_number?.startsWith('B'));
    const botOrders = dayOrders.filter(o => o.source === 'bot' || o.order_number?.startsWith('B'));
    const pendingOrders = dayOrders.filter(o => o.status !== 'dispatched');
    
    return {
      total: dayOrders.length,
      manual: manualOrders.length,
      bot: botOrders.length,
      dispatched: dayDispatches.length,
      pending: pendingOrders.length,
      totalQty: dayOrders.reduce((sum, o) => sum + (o.items?.reduce((s, i) => s + (i.qty || 0), 0) || o.total_qty || 0), 0),
      totalValue: dayOrders.reduce((sum, o) => sum + getOrderDisplayValue(o), 0),
      totalKgs: dayOrders.reduce((sum, o) => sum + getOrderTotalKgs(o), 0),
      rawOrderValue: dayOrders.reduce((sum, o) => sum + (o.items?.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0) || o.total_value || 0), 0),
      rawDispatchValue: dayDispatches.reduce((sum, o) => sum + (o.items?.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0) || o.total_value || 0), 0),
      orders: dayOrders,
      manualOrders,
      botOrders,
      dispatchedOrders: dayDispatches,
      dispatchKgs: dayDispatches.reduce((sum, o) => sum + getOrderTotalKgs(o), 0),
      pendingOrders,
      returns: dayReturns,
      returnQty: dayReturns.reduce((sum, r) => sum + (r.return_qty || 0), 0),
      returnValue: dayReturns.reduce((sum, r) => sum + (r.net_credit_value || r.return_value || 0), 0)
    };
  };

  // Get overall summary for current period
  const periodSummary = useMemo(() => {
    const days = viewMode === 'month' ? calendarDays.filter(d => isSameMonth(d, selectedDate)) : 
                 viewMode === 'week' ? weekDays : [selectedDate];
    let total = 0, dispatched = 0, pending = 0, totalOrderQty = 0, totalOrderValue = 0, totalDispatchQty = 0, totalDispatchValue = 0, totalReturnQty = 0, totalReturnValue = 0, totalReturnCount = 0, totalOrderKgs = 0, totalDispatchKgs = 0, totalRawOrderValue = 0, totalRawDispatchValue = 0;
    
    days.forEach(day => {
      const summary = getDaySummary(day);
      total += summary.total;
      dispatched += summary.dispatched;
      pending += summary.pending;
      totalOrderQty += summary.totalQty;
      totalOrderValue += summary.totalValue;
      totalOrderKgs += summary.totalKgs;
      totalRawOrderValue += summary.rawOrderValue;
      totalRawDispatchValue += summary.rawDispatchValue;
      totalReturnQty += summary.returnQty;
      totalReturnValue += summary.returnValue;
      totalReturnCount += summary.returns.length;
      // Dispatch qty & value
      const dQty = summary.dispatchedOrders?.reduce((s, o) => s + (o.items?.reduce((ss, i) => ss + (i.qty || 0), 0) || 0), 0) || 0;
      const dVal = summary.dispatchedOrders?.reduce((s, o) => s + getOrderDisplayValue(o), 0) || 0;
      totalDispatchQty += dQty;
      totalDispatchValue += dVal;
      totalDispatchKgs += summary.dispatchKgs;
    });
    
    return { total, dispatched, pending, totalOrderQty, totalOrderValue, totalOrderKgs, totalDispatchQty, totalDispatchValue, totalDispatchKgs, totalReturnQty, totalReturnValue, totalReturnCount, periodAvrg: totalOrderKgs > 0 ? totalRawOrderValue / totalOrderKgs : 0, dispatchAvrg: totalDispatchKgs > 0 ? totalRawDispatchValue / totalDispatchKgs : 0 };
  }, [orders, returnsData, selectedDate, viewMode, calendarDays, weekDays]);

  // Search for order by order number
  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResult(null);
      return;
    }
    const found = orders.find(o => 
      o.order_number?.toLowerCase().includes(searchQuery.toLowerCase().trim())
    );
    if (found) {
      setSearchResult(found);
      // Navigate calendar to the order's date
      const orderDate = new Date(found.order_date || found.created_at);
      setSelectedDate(orderDate);
      // Open day dialog for that date
      setSelectedDay(orderDate);
      setDayDialogOpen(true);
      toast.success(`Found ${found.order_number} on ${formatDateDDMonYY(orderDate)}`);
    } else {
      toast.error(`Order "${searchQuery}" not found`);
      setSearchResult(null);
    }
  };

  // Re-post order to Google Sheet
  const repostToGoogleSheet = async () => {
    if (!repostingOrder) return;
    
    setReposting(true);
    try {
      // Post to ORDER QTY and ORDER RATES sheets
      await axios.post(`${API}/orders/${repostingOrder.id}/post-to-sheets`);
      
      // If dispatched, also post to DISPATCH QTY sheet
      if (repostingOrder.status === 'dispatched') {
        await axios.post(`${API}/orders/${repostingOrder.id}/post-dispatch-to-sheets`);
      }
      
      toast.success(`Order ${repostingOrder.order_number} re-posted to Google Sheet`);
      setRepostDialogOpen(false);
      setRepostingOrder(null);
    } catch (error) {
      toast.error("Failed to re-post to Google Sheet");
    } finally {
      setReposting(false);
    }
  };

  const navigatePrev = () => {
    if (viewMode === 'month') setSelectedDate(subMonths(selectedDate, 1));
    else if (viewMode === 'week') setSelectedDate(subWeeks(selectedDate, 1));
    else setSelectedDate(addDays(selectedDate, -1));
  };

  const navigateNext = () => {
    if (viewMode === 'month') setSelectedDate(addMonths(selectedDate, 1));
    else if (viewMode === 'week') setSelectedDate(addWeeks(selectedDate, 1));
    else setSelectedDate(addDays(selectedDate, 1));
  };

  const getDateLabel = () => {
    if (viewMode === 'month') return format(selectedDate, 'MMMM yyyy');
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
    const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 0 });
    return `${formatDateDDMonYY(weekStart)} — ${formatDateDDMonYY(weekEnd)}`;
  };

  const openDayDialog = (date) => {
    setSelectedDay(date);
    setDayDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      confirmed: 'bg-green-100 text-green-800 border-green-300',
      dispatched: 'bg-blue-100 text-blue-800 border-blue-300',
      delivered: 'bg-purple-100 text-purple-800 border-purple-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100';
  };

  // Edit functions
  const startEditing = (order) => {
    setEditingOrder(order);
    setEditFormData({
      ...order,
      items: order.items?.map(item => ({ ...item })) || []
    });
  };

  const cancelEditing = () => {
    setEditingOrder(null);
    setEditFormData(null);
  };

  const updateEditItem = (index, field, value) => {
    const newItems = [...editFormData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === 'qty' || field === 'rate') {
      const qty = field === 'qty' ? Number(value) : Number(newItems[index].qty);
      const rate = field === 'rate' ? Number(value) : Number(newItems[index].rate);
      newItems[index].amount = qty * rate;
    }
    
    setEditFormData({ ...editFormData, items: newItems });
  };

  const addEditItem = () => {
    const newItems = [...editFormData.items, { grade: '', sku: 'TIN', packing: '10 KG', qty: 0, rate: 0, amount: 0 }];
    setEditFormData({ ...editFormData, items: newItems });
  };

  const removeEditItem = (index) => {
    const newItems = editFormData.items.filter((_, i) => i !== index);
    setEditFormData({ ...editFormData, items: newItems });
  };

  const saveOrder = async () => {
    if (!editFormData) return;
    
    setSaving(true);
    try {
      const totalQty = editFormData.items.reduce((sum, item) => sum + Number(item.qty || 0), 0);
      
      const updateData = {
        ...editFormData,
        total_qty: totalQty,
        items: editFormData.items.map(item => ({
          ...item,
          qty: Number(item.qty),
          rate: Number(item.rate),
          amount: Number(item.qty) * Number(item.rate)
        }))
      };
      
      // Let backend recalculate totals and discount based on payment_terms
      const grossValue = updateData.items.reduce((sum, item) => sum + item.amount, 0);
      updateData.total_value = grossValue;
      updateData.total_price = grossValue;

      await axios.put(`${API}/orders/${editFormData.id}`, updateData);
      toast.success(`Order ${editFormData.order_number} updated successfully`);
      
      // Store the order for repost dialog
      const savedOrder = { ...editFormData, ...updateData };
      
      await fetchOrders();
      cancelEditing();
      
      // Show repost dialog
      setRepostingOrder(savedOrder);
      setRepostDialogOpen(true);
    } catch (error) {
      toast.error("Failed to update order");
    } finally {
      setSaving(false);
    }
  };

  const grades = useMemo(() => {
    return [...new Set(products.map(p => p.grade))].filter(Boolean).sort();
  }, [products]);

  const skuTypes = ['TIN', 'BUCKET', 'TIN_REJ', 'VACUUM_10', 'VACUUM_20'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Render progress dots
  const renderProgressDots = (summary) => {
    const dots = [];
    // Orange dots for orders
    for (let i = 0; i < Math.min(summary.total, 12); i++) {
      dots.push(
        <span key={`order-${i}`} className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
      );
    }
    return dots;
  };

  // Selected day summary
  const selectedDaySummary = selectedDay ? getDaySummary(selectedDay) : null;

  return (
    <div className="p-4 md:p-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <CalendarIcon className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-xl font-bold text-foreground">Order Calendar</h1>
          <p className="text-muted-foreground text-xs">Track daily orders and dispatch status</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            placeholder="Search by Order Number (e.g., E03001)" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-9 h-9"
          />
        </div>
        <Button onClick={handleSearch} className="h-9">
          <Search className="w-4 h-4 mr-1" /> Search
        </Button>
        {searchQuery && (
          <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setSearchResult(null); }}>
            Clear
          </Button>
        )}
      </div>

      {/* Recent Invoices Section */}
      {recentInvoices.length > 0 && (
        <Card className="mb-4 border-slate-200" data-testid="recent-invoices-section">
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-600" />
              Recent Invoices
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 pt-0">
            <div className="grid gap-1.5">
              {recentInvoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-1.5 hover:bg-slate-100 transition-colors" data-testid={`recent-invoice-${inv.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-mono font-semibold text-emerald-700 shrink-0">{inv.invoice_number || inv.dispatch_number}</span>
                    <span className="text-xs text-muted-foreground truncate">{inv.customer_name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{inv.invoice_date || new Date(inv.generated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</span>
                    {inv.total_amount > 0 && <span className="text-xs font-medium shrink-0">Rs.{Number(inv.total_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 px-2 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 shrink-0"
                    onClick={() => handleDownloadInvoice(inv)}
                    disabled={downloadingInvoice === inv.id}
                    data-testid={`download-invoice-${inv.id}`}
                  >
                    {downloadingInvoice === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    <span className="ml-1 text-xs">Download</span>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation Bar */}
      <div className="flex items-center justify-between mb-4 py-3 border-b">
        <div className="flex items-center gap-2">
          {/* View Mode Buttons */}
          <div className="flex bg-secondary rounded-lg p-0.5">
            <Button 
              variant={viewMode === 'day' ? 'default' : 'ghost'} 
              size="sm" 
              className="h-7 px-3 text-xs"
              onClick={() => setViewMode('day')}
            >
              Day
            </Button>
            <Button 
              variant={viewMode === 'week' ? 'default' : 'ghost'} 
              size="sm" 
              className="h-7 px-3 text-xs"
              onClick={() => setViewMode('week')}
            >
              Week
            </Button>
            <Button 
              variant={viewMode === 'month' ? 'default' : 'ghost'} 
              size="sm" 
              className="h-7 px-3 text-xs"
              onClick={() => setViewMode('month')}
            >
              Month
            </Button>
          </div>

          {/* Date Navigation */}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={navigatePrev}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="font-semibold text-sm min-w-[140px] text-center">{getDateLabel()}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={navigateNext}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setSelectedDate(new Date())}>
            Today
          </Button>
        </div>

        {/* Legend & Stats */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500"></span> Dispatched
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span> Orders
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span> Pending
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-white text-xs">
              <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
              {periodSummary.dispatched}
            </Badge>
            <Badge variant="outline" className="bg-white text-xs">
              <span className="w-2 h-2 rounded-full bg-amber-400 mr-1"></span>
              {periodSummary.total}
            </Badge>
            <Badge variant="outline" className="bg-white text-xs">
              <span className="w-2 h-2 rounded-full bg-blue-500 mr-1"></span>
              {periodSummary.pending}
            </Badge>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchOrders}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Day View - Selected Date Details */}
          {viewMode === 'day' && (
            <div className="space-y-4">
              {/* Day Header Card */}
              <Card className="bg-gradient-to-r from-primary/5 to-green-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold">{format(selectedDate, 'EEEE')}</h2>
                      <p className="text-lg text-muted-foreground">{formatDateDDMonYY(selectedDate)}</p>
                    </div>
                    <Badge className={getDaySummary(selectedDate).pending === 0 && getDaySummary(selectedDate).total > 0 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                      {getDaySummary(selectedDate).pending === 0 && getDaySummary(selectedDate).total > 0 ? 'All Dispatched' : `${getDaySummary(selectedDate).pending} Pending`}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Day Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <ShoppingCart className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                    <p className="text-xs text-muted-foreground">Order Qty</p>
                    <p className="text-2xl font-bold">{getDaySummary(selectedDate).totalQty}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <ShoppingCart className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                    <p className="text-xs text-muted-foreground">Order Value</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(getDaySummary(selectedDate).totalValue)}</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-200">
                  <CardContent className="p-4 text-center">
                    <Package className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                    <p className="text-xs text-muted-foreground">Day AVRG</p>
                    <p className="text-xl font-bold text-blue-700">{(() => { const s = getDaySummary(selectedDate); return s.totalKgs > 0 ? formatCurrency(Math.round(s.rawOrderValue / s.totalKgs)) : '₹0/-'; })()}</p>
                    <p className="text-[10px] text-muted-foreground">{getDaySummary(selectedDate).totalKgs.toLocaleString('en-IN')} KGs</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Truck className="w-6 h-6 mx-auto mb-2 text-green-500" />
                    <p className="text-xs text-muted-foreground">Dispatch Qty</p>
                    <p className="text-2xl font-bold">{getDaySummary(selectedDate).dispatchedOrders?.reduce((s, o) => s + (o.items?.reduce((ss, i) => ss + (i.qty || 0), 0) || 0), 0) || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Truck className="w-6 h-6 mx-auto mb-2 text-green-500" />
                    <p className="text-xs text-muted-foreground">Dispatch Value</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(getDaySummary(selectedDate).dispatchedOrders?.reduce((s, o) => s + getOrderDisplayValue(o), 0) || 0)}</p>
                  </CardContent>
                </Card>
                {getDaySummary(selectedDate).dispatchKgs > 0 && (
                <Card className="border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <Truck className="w-6 h-6 mx-auto mb-2 text-emerald-600" />
                    <p className="text-xs text-muted-foreground">Disp AVRG</p>
                    <p className="text-xl font-bold text-emerald-700">{(() => { const s = getDaySummary(selectedDate); return s.dispatchKgs > 0 ? formatCurrency(Math.round(s.rawDispatchValue / s.dispatchKgs)) : '₹0/-'; })()}</p>
                    <p className="text-[10px] text-muted-foreground">{getDaySummary(selectedDate).dispatchKgs.toLocaleString('en-IN')} KGs</p>
                  </CardContent>
                </Card>
                )}
                {getDaySummary(selectedDate).returns.length > 0 && (
                <Card className="border-red-200">
                  <CardContent className="p-4 text-center">
                    <RotateCcw className="w-6 h-6 mx-auto mb-2 text-red-500" />
                    <p className="text-xs text-muted-foreground">Returns</p>
                    <p className="text-2xl font-bold text-red-600">{getDaySummary(selectedDate).returnQty} qty</p>
                    <p className="text-xs text-red-500">{formatCurrency(getDaySummary(selectedDate).returnValue)}</p>
                  </CardContent>
                </Card>
                )}
              </div>

              {/* Orders Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-amber-500" />
                    All Orders ({getDaySummary(selectedDate).total})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {getDaySummary(selectedDate).orders.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/50">
                          <TableHead className="text-xs">Order #</TableHead>
                          <TableHead className="text-xs">Customer</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-right">Value</TableHead>
                          <TableHead className="text-xs text-right text-blue-700">AVRG</TableHead>
                          <TableHead className="text-xs text-center">Status</TableHead>
                          <TableHead className="text-xs text-center">Edit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getDaySummary(selectedDate).orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell className="text-xs font-mono font-medium">{order.order_number}</TableCell>
                            <TableCell className="text-xs">{order.dispatch_name || order.customer_name}</TableCell>
                            <TableCell className="text-xs text-right font-medium">
                              {order.items?.reduce((s, i) => s + (i.qty || 0), 0) || order.total_qty || 0}
                            </TableCell>
                            <TableCell className="text-xs text-right font-medium text-green-600">
                              {formatCurrency(getOrderDisplayValue(order))}
                            </TableCell>
                            <TableCell className="text-xs text-right font-bold text-blue-700">
                              {(() => { const kgs = getOrderTotalKgs(order); const val = (order.items || []).reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0); return kgs > 0 ? Math.round(val / kgs).toLocaleString('en-IN') : '-'; })()}
                            </TableCell>
                            <TableCell className="text-xs text-center">
                              <Badge variant="outline" className={`text-[10px] ${getStatusBadge(order.status)}`}>
                                {order.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-center">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => startEditing(order)}>
                                <Edit className="w-3 h-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Package className="w-12 h-12 mb-3 opacity-30" />
                      <p className="font-medium">No orders on this day</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Returns Table for this day */}
              {getDaySummary(selectedDate).returns.length > 0 && (
              <Card className="border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <RotateCcw className="w-5 h-5 text-red-500" />
                    Returns ({getDaySummary(selectedDate).returns.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-red-50/50">
                        <TableHead className="text-xs">Return #</TableHead>
                        <TableHead className="text-xs">Order #</TableHead>
                        <TableHead className="text-xs">Customer</TableHead>
                        <TableHead className="text-xs">Items</TableHead>
                        <TableHead className="text-xs text-right">Qty</TableHead>
                        <TableHead className="text-xs text-right">Credit</TableHead>
                        <TableHead className="text-xs text-center">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getDaySummary(selectedDate).returns.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="text-xs font-mono font-semibold text-blue-700">{r.return_number}</TableCell>
                          <TableCell className="text-xs font-mono">{r.order_number}</TableCell>
                          <TableCell className="text-xs">{r.dispatch_name || r.customer_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{(r.items || []).map(i => `${i.grade} x${i.qty}`).join(', ')}</TableCell>
                          <TableCell className="text-xs text-right font-bold">{r.return_qty}</TableCell>
                          <TableCell className="text-xs text-right font-bold text-red-600">{formatCurrency(r.net_credit_value || r.return_value)}</TableCell>
                          <TableCell className="text-xs text-center">
                            <Badge className={r.status === 'confirmed' ? 'bg-green-100 text-green-800 border-green-300 text-[9px]' : 'bg-amber-100 text-amber-800 border-amber-300 text-[9px]'}>
                              {r.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              )}
            </div>
          )}

          {/* Month View - Calendar Grid */}
          {viewMode === 'month' && (
            <div className="space-y-4">
            {/* Month Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="border-amber-200 bg-amber-50/40">
                <CardContent className="p-3 text-center">
                  <ShoppingCart className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Order Qty</p>
                  <p className="text-xl font-bold text-amber-700">{periodSummary.totalOrderQty.toLocaleString('en-IN')}</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50/40">
                <CardContent className="p-3 text-center">
                  <ShoppingCart className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Order Value</p>
                  <p className="text-lg font-bold text-amber-700">{formatCurrency(periodSummary.totalOrderValue)}</p>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-blue-50/40">
                <CardContent className="p-3 text-center">
                  <Package className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">AVRG</p>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(Math.round(periodSummary.periodAvrg))}</p>
                  <p className="text-[9px] text-muted-foreground">{periodSummary.totalOrderKgs.toLocaleString('en-IN')} KGs</p>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50/40">
                <CardContent className="p-3 text-center">
                  <Truck className="w-5 h-5 mx-auto mb-1 text-green-600" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Dispatch Qty</p>
                  <p className="text-xl font-bold text-green-700">{periodSummary.totalDispatchQty.toLocaleString('en-IN')}</p>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50/40">
                <CardContent className="p-3 text-center">
                  <Truck className="w-5 h-5 mx-auto mb-1 text-green-600" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Dispatch Value</p>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(periodSummary.totalDispatchValue)}</p>
                </CardContent>
              </Card>
              {periodSummary.totalDispatchKgs > 0 && (
              <Card className="border-emerald-200 bg-emerald-50/40">
                <CardContent className="p-3 text-center">
                  <Truck className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Disp AVRG</p>
                  <p className="text-lg font-bold text-emerald-700">{formatCurrency(Math.round(periodSummary.dispatchAvrg))}</p>
                  <p className="text-[9px] text-muted-foreground">{periodSummary.totalDispatchKgs.toLocaleString('en-IN')} KGs</p>
                </CardContent>
              </Card>
              )}
              {periodSummary.totalReturnCount > 0 && (
              <Card className="border-red-200 bg-red-50/40">
                <CardContent className="p-3 text-center">
                  <RotateCcw className="w-5 h-5 mx-auto mb-1 text-red-500" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Returns ({periodSummary.totalReturnCount})</p>
                  <p className="text-xl font-bold text-red-600">{periodSummary.totalReturnQty.toLocaleString('en-IN')} qty</p>
                  <p className="text-xs text-red-500">{formatCurrency(periodSummary.totalReturnValue)}</p>
                </CardContent>
              </Card>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden">
              {/* Day Headers */}
              <div className="grid grid-cols-7 bg-secondary/50 border-b">
                {dayNames.map(day => (
                  <div key={day} className="p-2 text-center text-xs font-semibold text-muted-foreground border-r last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map((day, idx) => {
                  const summary = getDaySummary(day);
                  const isCurrentMonth = isSameMonth(day, selectedDate);
                  const isToday = isSameDay(day, new Date());
                  const dispatchQty = summary.dispatchedOrders?.reduce((s, o) => s + (o.items?.reduce((ss, i) => ss + (i.qty || 0), 0) || 0), 0) || 0;
                  const dispatchValue = summary.dispatchedOrders?.reduce((s, o) => s + getOrderDisplayValue(o), 0) || 0;
                  
                  return (
                    <div 
                      key={idx}
                      onClick={() => openDayDialog(day)}
                      className={`
                        min-h-[130px] p-2 border-r border-b last:border-r-0 cursor-pointer
                        transition-colors hover:bg-secondary/30
                        ${!isCurrentMonth ? 'bg-gray-50 opacity-50' : 'bg-white'}
                        ${isToday ? 'ring-2 ring-primary ring-inset bg-primary/5' : ''}
                      `}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <span className={`text-sm font-bold ${isToday ? 'text-primary' : ''}`}>
                          {format(day, 'd')}
                        </span>
                        {summary.pending === 0 && summary.total > 0 && (
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        )}
                        {summary.pending > 0 && (
                          <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                        )}
                      </div>
                      
                      {(summary.total > 0 || summary.dispatched > 0 || summary.returns.length > 0) && (
                        <div className="space-y-0.5 text-[8px]">
                          {/* Order Stats */}
                          {summary.total > 0 && (
                            <>
                              <div className="flex items-center justify-between text-amber-700">
                                <span>Ord:</span>
                                <span className="font-semibold">{summary.totalQty} qty</span>
                              </div>
                              <div className="text-right text-amber-600 font-medium truncate" title={formatCurrency(summary.totalValue)}>
                                {formatCurrency(summary.totalValue)}
                              </div>
                              <div className="text-right text-blue-600 font-medium text-[7px]" title={`AVRG: ${summary.totalKgs > 0 ? formatCurrency(Math.round(summary.rawOrderValue / summary.totalKgs)) : '0'}`}>
                                A:{summary.totalKgs > 0 ? Math.round(summary.rawOrderValue / summary.totalKgs).toLocaleString('en-IN') : '0'}
                              </div>
                            </>
                          )}
                          
                          {/* Dispatch Stats */}
                          {summary.dispatched > 0 && (
                            <>
                              <div className="flex items-center justify-between text-green-700 pt-0.5 border-t border-dashed">
                                <span>Disp:</span>
                                <span className="font-semibold">{dispatchQty} qty</span>
                              </div>
                              <div className="text-right text-green-600 font-medium truncate" title={formatCurrency(dispatchValue)}>
                                {formatCurrency(dispatchValue)}
                              </div>
                              <div className="text-right text-emerald-600 font-medium text-[7px]" title={`D.AVRG: ${summary.dispatchKgs > 0 ? formatCurrency(Math.round(summary.rawDispatchValue / summary.dispatchKgs)) : '0'}`}>
                                DA:{summary.dispatchKgs > 0 ? Math.round(summary.rawDispatchValue / summary.dispatchKgs).toLocaleString('en-IN') : '0'}
                              </div>
                            </>
                          )}
                          
                          {/* Return Stats */}
                          {summary.returns.length > 0 && (
                            <>
                              <div className="flex items-center justify-between text-red-600 pt-0.5 border-t border-dashed">
                                <span>Ret:</span>
                                <span className="font-semibold">{summary.returnQty} qty</span>
                              </div>
                              <div className="text-right text-red-500 font-medium truncate" title={formatCurrency(summary.returnValue)}>
                                {formatCurrency(summary.returnValue)}
                              </div>
                            </>
                          )}
                          
                          {/* Count */}
                          <div className="text-center pt-1 text-[7px] text-muted-foreground">
                            {summary.total > 0 && <span>{summary.total} ord</span>}
                            {summary.total > 0 && summary.dispatched > 0 && <span> / </span>}
                            {summary.dispatched > 0 && <span>{summary.dispatched} disp</span>}
                            {summary.returns.length > 0 && <span> / {summary.returns.length} ret</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
          )}

          {/* Week View - Vertical Columns like Production Calendar */}
          {viewMode === 'week' && (
            <div className="space-y-4">
            {/* Week Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="border-amber-200 bg-amber-50/40">
                <CardContent className="p-3 text-center">
                  <ShoppingCart className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Order Qty</p>
                  <p className="text-xl font-bold text-amber-700">{periodSummary.totalOrderQty.toLocaleString('en-IN')}</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50/40">
                <CardContent className="p-3 text-center">
                  <ShoppingCart className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Order Value</p>
                  <p className="text-lg font-bold text-amber-700">{formatCurrency(periodSummary.totalOrderValue)}</p>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-blue-50/40">
                <CardContent className="p-3 text-center">
                  <Package className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">AVRG</p>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(Math.round(periodSummary.periodAvrg))}</p>
                  <p className="text-[9px] text-muted-foreground">{periodSummary.totalOrderKgs.toLocaleString('en-IN')} KGs</p>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50/40">
                <CardContent className="p-3 text-center">
                  <Truck className="w-5 h-5 mx-auto mb-1 text-green-600" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Dispatch Qty</p>
                  <p className="text-xl font-bold text-green-700">{periodSummary.totalDispatchQty.toLocaleString('en-IN')}</p>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50/40">
                <CardContent className="p-3 text-center">
                  <Truck className="w-5 h-5 mx-auto mb-1 text-green-600" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Dispatch Value</p>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(periodSummary.totalDispatchValue)}</p>
                </CardContent>
              </Card>
              {periodSummary.totalDispatchKgs > 0 && (
              <Card className="border-emerald-200 bg-emerald-50/40">
                <CardContent className="p-3 text-center">
                  <Truck className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Disp AVRG</p>
                  <p className="text-lg font-bold text-emerald-700">{formatCurrency(Math.round(periodSummary.dispatchAvrg))}</p>
                  <p className="text-[9px] text-muted-foreground">{periodSummary.totalDispatchKgs.toLocaleString('en-IN')} KGs</p>
                </CardContent>
              </Card>
              )}
              {periodSummary.totalReturnCount > 0 && (
              <Card className="border-red-200 bg-red-50/40">
                <CardContent className="p-3 text-center">
                  <RotateCcw className="w-5 h-5 mx-auto mb-1 text-red-500" />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Returns ({periodSummary.totalReturnCount})</p>
                  <p className="text-xl font-bold text-red-600">{periodSummary.totalReturnQty.toLocaleString('en-IN')} qty</p>
                  <p className="text-xs text-red-500">{formatCurrency(periodSummary.totalReturnValue)}</p>
                </CardContent>
              </Card>
              )}
            </div>

            <div className="grid grid-cols-7 gap-3">
              {weekDays.map((day, idx) => {
                const summary = getDaySummary(day);
                const isToday = isSameDay(day, new Date());
                const pending = summary.pending;
                
                return (
                  <div 
                    key={idx}
                    onClick={() => openDayDialog(day)}
                    className={`
                      min-h-[450px] rounded-lg border-2 cursor-pointer
                      transition-all hover:shadow-md
                      ${isToday ? 'border-amber-400 bg-amber-50/30' : 'border-gray-200 bg-white'}
                    `}
                  >
                    {/* Day Header */}
                    <div className={`p-3 border-b ${isToday ? 'bg-amber-50' : ''}`}>
                      <div className="text-[10px] text-muted-foreground uppercase">{dayNames[idx]}</div>
                      <div className={`font-bold text-base ${isToday ? 'text-amber-700' : ''}`}>
                        {formatDateDDMonYY(day)}
                      </div>
                    </div>
                    
                    {/* Day Content - Section List */}
                    <div className="p-3 space-y-2">
                      {/* Total Orders */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${summary.total > 0 ? 'bg-amber-400' : 'bg-gray-300'}`}></span>
                        <span className="text-gray-700">Total Orders</span>
                        <span className="ml-auto font-medium">{summary.total}</span>
                      </div>
                      
                      {/* Manual Orders */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${summary.manual > 0 ? 'bg-blue-400' : 'bg-gray-300'}`}></span>
                        <span className="text-gray-700">Manual</span>
                        <span className="ml-auto font-medium">{summary.manual}</span>
                      </div>
                      
                      {/* Bot Orders */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${summary.bot > 0 ? 'bg-purple-400' : 'bg-gray-300'}`}></span>
                        <span className="text-gray-700">Bot Orders</span>
                        <span className="ml-auto font-medium">{summary.bot}</span>
                      </div>
                      
                      {/* Dispatched */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${summary.dispatched > 0 ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        <span className="text-gray-700">Dispatched</span>
                        <span className="ml-auto font-medium">{summary.dispatched}</span>
                      </div>
                      
                      {/* Pending */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${pending > 0 ? 'bg-amber-500' : 'bg-gray-300'}`}></span>
                        <span className="text-gray-700">Pending</span>
                        <span className="ml-auto font-medium">{pending}</span>
                      </div>
                      
                      {/* Qty */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${summary.totalQty > 0 ? 'bg-teal-400' : 'bg-gray-300'}`}></span>
                        <span className="text-gray-700">Total Qty</span>
                        <span className="ml-auto font-medium">{summary.totalQty}</span>
                      </div>
                      
                      {/* Value */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${summary.totalValue > 0 ? 'bg-green-400' : 'bg-gray-300'}`}></span>
                        <span className="text-gray-700">Value</span>
                        <span className="ml-auto font-medium text-green-600 text-[10px]" title={formatCurrency(summary.totalValue)}>
                          {formatCurrency(summary.totalValue)}
                        </span>
                      </div>
                      
                      {/* AVRG */}
                      {summary.totalKgs > 0 && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        <span className="text-blue-700 font-medium">AVRG</span>
                        <span className="ml-auto font-bold text-blue-700 text-[10px]">{Math.round(summary.rawOrderValue / summary.totalKgs).toLocaleString('en-IN')}</span>
                      </div>
                      )}
                      
                      {/* Dispatch Qty */}
                      <div className="flex items-center gap-2 text-xs pt-1 border-t border-dashed">
                        <span className={`w-2 h-2 rounded-full ${summary.dispatchedOrders?.length > 0 ? 'bg-green-600' : 'bg-gray-300'}`}></span>
                        <span className="text-gray-700">Disp Qty</span>
                        <span className="ml-auto font-medium text-green-700">{summary.dispatchedOrders?.reduce((s, o) => s + (o.items?.reduce((ss, i) => ss + (i.qty || 0), 0) || 0), 0) || 0}</span>
                      </div>
                      
                      {/* Dispatch Value */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${summary.dispatchedOrders?.length > 0 ? 'bg-green-600' : 'bg-gray-300'}`}></span>
                        <span className="text-gray-700">Disp Val</span>
                        <span className="ml-auto font-medium text-green-700 text-[10px]" title={formatCurrency(summary.dispatchedOrders?.reduce((s, o) => s + getOrderDisplayValue(o), 0) || 0)}>
                          {formatCurrency(summary.dispatchedOrders?.reduce((s, o) => s + getOrderDisplayValue(o), 0) || 0)}
                        </span>
                      </div>
                      
                      {/* Dispatch AVRG */}
                      {summary.dispatchKgs > 0 && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        <span className="text-emerald-700 font-medium">D.AVRG</span>
                        <span className="ml-auto font-bold text-emerald-700 text-[10px]">{Math.round(summary.rawDispatchValue / summary.dispatchKgs).toLocaleString('en-IN')}</span>
                      </div>
                      )}
                      
                      {/* Returns */}
                      {summary.returns.length > 0 && (
                        <>
                          <div className="flex items-center gap-2 text-xs pt-1 border-t border-dashed">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                            <span className="text-red-600 font-medium">Returns</span>
                            <span className="ml-auto font-medium text-red-600">{summary.returns.length}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="w-2 h-2 rounded-full bg-red-400"></span>
                            <span className="text-gray-700">Ret Qty</span>
                            <span className="ml-auto font-medium text-red-600">{summary.returnQty}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="w-2 h-2 rounded-full bg-red-400"></span>
                            <span className="text-gray-700">Ret Val</span>
                            <span className="ml-auto font-medium text-red-500 text-[10px]">{formatCurrency(summary.returnValue)}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Summary Footer */}
                    {summary.total > 0 && (
                      <div className="px-3 pb-3 mt-auto">
                        <div className="text-[9px] text-amber-600 leading-tight">
                          {pending > 0 ? (
                            <span>Pending: {pending} orders, {summary.totalQty - (summary.dispatchedOrders?.reduce((s, o) => s + (o.items?.reduce((ss, i) => ss + (i.qty || 0), 0) || 0), 0) || 0)} qty</span>
                          ) : (
                            <span className="text-green-600">All orders dispatched</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </div>
          )}
        </>
      )}

      {/* Day Detail Dialog */}
      <Dialog open={dayDialogOpen} onOpenChange={setDayDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                {selectedDay && formatDateDDMonYY(selectedDay)}
              </DialogTitle>
              {selectedDaySummary && (
                <Badge className={selectedDaySummary.pending === 0 && selectedDaySummary.total > 0 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                  {selectedDaySummary.pending === 0 && selectedDaySummary.total > 0 ? 'All Dispatched' : `${selectedDaySummary.pending} Pending`}
                </Badge>
              )}
            </div>
            {selectedDaySummary && (
              <p className="text-sm text-muted-foreground">
                {selectedDaySummary.total} orders, {selectedDaySummary.dispatched} dispatched
              </p>
            )}
          </DialogHeader>
          
          {selectedDaySummary && (
            <ScrollArea className="max-h-[55vh]">
              <div className="space-y-3">
                {/* Orders Section */}
                <div className="bg-amber-50 rounded-lg border border-amber-200 overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 cursor-pointer select-none" onClick={() => setOrdersExpanded(!ordersExpanded)}>
                    <div className="flex items-center gap-2">
                      {ordersExpanded ? <ChevronUp className="w-4 h-4 text-amber-600" /> : <ChevronDown className="w-4 h-4 text-amber-600" />}
                      <ShoppingCart className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-amber-800">All Orders</span>
                      <Badge className="bg-amber-100 text-amber-800 text-[10px]">{selectedDaySummary.total}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-amber-700">
                      <span>Qty: <strong>{selectedDaySummary.totalQty}</strong></span>
                      <span>Value: <strong>{formatCurrency(selectedDaySummary.totalValue)}</strong></span>
                    </div>
                  </div>
                  
                  {ordersExpanded && (
                    <div className="px-3 pb-3">
                      {selectedDaySummary.orders.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-100/50">
                          <TableHead className="text-xs">Order #</TableHead>
                          <TableHead className="text-xs">Customer</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-right">Value</TableHead>
                          <TableHead className="text-xs text-center">Status</TableHead>
                          <TableHead className="text-xs text-center">Edit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedDaySummary.orders.map((order) => (
                          <TableRow key={order.id} className="bg-white">
                            <TableCell className="text-xs font-mono font-medium">{order.order_number}</TableCell>
                            <TableCell className="text-xs">{order.dispatch_name || order.customer_name}</TableCell>
                            <TableCell className="text-xs text-right font-medium">
                              {order.items?.reduce((s, i) => s + (i.qty || 0), 0) || order.total_qty || 0}
                            </TableCell>
                            <TableCell className="text-xs text-right font-medium text-green-600">
                              {formatCurrency(getOrderDisplayValue(order))}
                            </TableCell>
                            <TableCell className="text-xs text-center">
                              <Badge variant="outline" className={`text-[10px] ${getStatusBadge(order.status)}`}>
                                {order.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-center">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => startEditing(order)}>
                                <Edit className="w-3 h-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                        <p className="text-sm text-amber-600 text-center py-4">No orders</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Dispatched Section */}
                <div className="bg-green-50 rounded-lg border border-green-200 overflow-hidden">
                  {(() => {
                    const dispQty = selectedDaySummary.dispatchedOrders?.reduce((s, o) => s + (o.items?.reduce((ss, i) => ss + (i.qty || 0), 0) || 0), 0) || 0;
                    const dispValue = selectedDaySummary.dispatchedOrders?.reduce((s, o) => s + getOrderDisplayValue(o), 0) || 0;
                    return (
                      <div className="flex items-center justify-between px-3 py-2 cursor-pointer select-none" onClick={() => setDispatchExpanded(!dispatchExpanded)}>
                        <div className="flex items-center gap-2">
                          {dispatchExpanded ? <ChevronUp className="w-4 h-4 text-green-600" /> : <ChevronDown className="w-4 h-4 text-green-600" />}
                          <Truck className="w-4 h-4 text-green-600" />
                          <span className="font-semibold text-green-800">Dispatched</span>
                          <Badge className="bg-green-100 text-green-800 text-[10px]">{selectedDaySummary.dispatched}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-green-700">
                          <span>Qty: <strong>{dispQty}</strong></span>
                          <span>Value: <strong>{formatCurrency(dispValue)}</strong></span>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {dispatchExpanded && (
                    <div className="px-3 pb-3">
                      {selectedDaySummary.dispatchedOrders.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-green-100/50">
                          <TableHead className="text-xs">Order #</TableHead>
                          <TableHead className="text-xs">Dispatch #</TableHead>
                          <TableHead className="text-xs">Customer</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-right">Value</TableHead>
                          <TableHead className="text-xs text-center">Edit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedDaySummary.dispatchedOrders.map((order) => (
                          <TableRow key={order.id} className="bg-white">
                            <TableCell className="text-xs font-mono font-medium">{order.order_number}</TableCell>
                            <TableCell className="text-xs font-mono text-blue-600">{order.dispatch_number || '-'}</TableCell>
                            <TableCell className="text-xs">{order.dispatch_name || order.customer_name}</TableCell>
                            <TableCell className="text-xs text-right font-medium">
                              {order.items?.reduce((s, i) => s + (i.qty || 0), 0) || order.total_qty || 0}
                            </TableCell>
                            <TableCell className="text-xs text-right font-medium text-green-600">
                              {formatCurrency(getOrderDisplayValue(order))}
                            </TableCell>
                            <TableCell className="text-xs text-center">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => startEditing(order)}>
                                <Edit className="w-3 h-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                        <p className="text-sm text-green-600 text-center py-4">No dispatched orders</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Returns Section */}
                {selectedDaySummary.returns.length > 0 && (
                <div className="bg-red-50 rounded-lg border border-red-200 overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <RotateCcw className="w-4 h-4 text-red-500" />
                      <span className="font-semibold text-red-800">Returns</span>
                      <Badge className="bg-red-100 text-red-800 text-[10px]">{selectedDaySummary.returns.length}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-red-700">
                      <span>Qty: <strong>{selectedDaySummary.returnQty}</strong></span>
                      <span>Credit: <strong>{formatCurrency(selectedDaySummary.returnValue)}</strong></span>
                    </div>
                  </div>
                  <div className="px-3 pb-3">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-red-100/50">
                          <TableHead className="text-xs">Return #</TableHead>
                          <TableHead className="text-xs">Order #</TableHead>
                          <TableHead className="text-xs">Customer</TableHead>
                          <TableHead className="text-xs">Items</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedDaySummary.returns.map((r) => (
                          <TableRow key={r.id} className="bg-white">
                            <TableCell className="text-xs font-mono font-semibold text-blue-700">{r.return_number}</TableCell>
                            <TableCell className="text-xs font-mono">{r.order_number}</TableCell>
                            <TableCell className="text-xs">{r.dispatch_name || r.customer_name}</TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">{(r.items || []).map(i => `${i.grade} x${i.qty}`).join(', ')}</TableCell>
                            <TableCell className="text-xs text-right font-bold">{r.return_qty}</TableCell>
                            <TableCell className="text-xs text-center">
                              <Badge className={r.status === 'confirmed' ? 'bg-green-100 text-green-800 text-[9px]' : 'bg-amber-100 text-amber-800 text-[9px]'}>
                                {r.status === 'confirmed' ? 'Done' : 'Pending'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                )}

                {/* Summary */}
                <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                  <div className="text-sm">
                    <span>Total Qty: <strong>{selectedDaySummary.totalQty}</strong></span>
                  </div>
                  <div className="text-sm">
                    <span>Total Value: <strong className="text-green-600">{formatCurrency(selectedDaySummary.totalValue)}</strong></span>
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDayDialogOpen(false)}>
              Close Day
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={!!editingOrder} onOpenChange={(open) => !open && cancelEditing()}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Order {editFormData?.order_number}
              {editFormData?.status === 'dispatched' && (
                <Badge className="ml-2 bg-blue-100 text-blue-800">Dispatched - {editFormData?.dispatch_number}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {editFormData && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 p-1">
                {/* Customer Info */}
                <div className="grid grid-cols-2 gap-4 p-3 bg-secondary/30 rounded-lg">
                  <div>
                    <Label className="text-xs">Customer</Label>
                    <p className="font-medium">{editFormData.dispatch_name || editFormData.customer_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Badge className={getStatusBadge(editFormData.status)}>{editFormData.status}</Badge>
                  </div>
                </div>

                {/* Order Details */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Payment Terms</Label>
                    <Select 
                      value={editFormData.payment_terms || ''} 
                      onValueChange={(v) => setEditFormData({...editFormData, payment_terms: v})}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ADV">Advance (ADV)</SelectItem>
                        <SelectItem value="COD">Cash on Delivery (COD)</SelectItem>
                        <SelectItem value="CREDIT">Credit</SelectItem>
                        <SelectItem value="RDAP">RDAP</SelectItem>
                        <SelectItem value="DDAP RECVD">DDAP RECVD (with Discount)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Bill Type</Label>
                    <Select 
                      value={editFormData.est_type || 'EST'} 
                      onValueChange={(v) => setEditFormData({...editFormData, est_type: v})}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EST">Estimate (EST)</SelectItem>
                        <SelectItem value="INV">Invoice (INV)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Transport</Label>
                    <Input 
                      value={editFormData.transport_name || ''} 
                      onChange={(e) => setEditFormData({...editFormData, transport_name: e.target.value})}
                      className="h-8 text-sm"
                      placeholder="Transport name"
                    />
                  </div>
                </div>

                {/* Items Table */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-semibold">Order Items</Label>
                    <Button size="sm" variant="outline" onClick={addEditItem} className="h-7 text-xs">
                      <Plus className="w-3 h-3 mr-1" /> Add Item
                    </Button>
                  </div>
                  
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/50">
                        <TableHead className="text-xs w-[120px]">Grade</TableHead>
                        <TableHead className="text-xs w-[100px]">SKU</TableHead>
                        <TableHead className="text-xs w-[80px] text-right">Qty</TableHead>
                        <TableHead className="text-xs w-[100px] text-right">Rate (₹)</TableHead>
                        <TableHead className="text-xs w-[100px] text-right">Amount</TableHead>
                        <TableHead className="text-xs w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editFormData.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="p-1">
                            <Select value={item.grade || ''} onValueChange={(v) => updateEditItem(idx, 'grade', v)}>
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Grade" />
                              </SelectTrigger>
                              <SelectContent>
                                {grades.map(g => (
                                  <SelectItem key={g} value={g}>{g}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="p-1">
                            <Select value={item.sku || 'TIN'} onValueChange={(v) => updateEditItem(idx, 'sku', v)}>
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {skuTypes.map(s => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="p-1">
                            <Input 
                              type="number" 
                              value={item.qty || ''} 
                              onChange={(e) => updateEditItem(idx, 'qty', e.target.value)}
                              className="h-8 text-xs text-right w-full"
                            />
                          </TableCell>
                          <TableCell className="p-1">
                            <Input 
                              type="number" 
                              value={item.rate || ''} 
                              onChange={(e) => updateEditItem(idx, 'rate', e.target.value)}
                              className="h-8 text-xs text-right w-full"
                            />
                          </TableCell>
                          <TableCell className="p-1 text-right font-medium text-sm">
                            {formatCurrency(item.amount || (item.qty * item.rate) || 0)}
                          </TableCell>
                          <TableCell className="p-1">
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removeEditItem(idx)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Totals */}
                  <div className="flex justify-end mt-3 p-3 bg-green-50 rounded-lg">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Total Qty: <strong>{editFormData.items?.reduce((s, i) => s + Number(i.qty || 0), 0)}</strong></p>
                      {editFormData.payment_terms === 'DDAP RECVD' && (
                        <p className="text-xs text-amber-600">Discount (DDAP): will be calculated on save</p>
                      )}
                      <p className="text-lg font-bold text-green-700">
                        Total: {formatCurrency(editFormData.items?.reduce((s, i) => s + (Number(i.qty || 0) * Number(i.rate || 0)), 0))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelEditing}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={saveOrder} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-post to Google Sheet Dialog */}
      <Dialog open={repostDialogOpen} onOpenChange={setRepostDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Re-post to Google Sheet?
            </DialogTitle>
            <DialogDescription>
              Order <strong>{repostingOrder?.order_number}</strong> has been updated. 
              Would you like to re-post it to Google Sheet? 
              This will update the existing row (no duplicate will be created).
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-3 bg-secondary/30 rounded-lg text-sm">
            <p><strong>Order:</strong> {repostingOrder?.order_number}</p>
            <p><strong>Customer:</strong> {repostingOrder?.dispatch_name || repostingOrder?.customer_name}</p>
            <p><strong>Value:</strong> {formatCurrency(repostingOrder?.invoice_value || repostingOrder?.total_value)}</p>
            {repostingOrder?.status === 'dispatched' && (
              <p className="text-green-600 mt-1">
                <Truck className="w-3 h-3 inline mr-1" />
                Dispatched - will also update DISPATCH QTY sheet
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setRepostDialogOpen(false); setRepostingOrder(null); }}>
              Skip
            </Button>
            <Button onClick={repostToGoogleSheet} disabled={reposting} className="bg-green-600 hover:bg-green-700">
              {reposting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
              Re-post to Sheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
